import { useApiQuery } from '../../../api';
import { countryCodeOffsetMapping, useBuildVars } from 'src/config';

export interface Pagination {
  PageNumber: number;
  PageSize: number;
}
export interface BidSearchRequest extends Pagination {
  BCN?: string;
  BidNumber?: string;
  CurrentStatus?: string;
  Filter?: string;
  FromDate?: string;
  QuoteNumber?: string;
  SortColumn?: string;
  SortOrder?: string;
  ToDate?: string;
  Vendor?: string;
  VendorProgram?: string;
}

export type BidSearchQuery = {
  BCN?: string;
  BidNumber?: string;
  CurrentStatus?: number[];
  FromDate?: string;
  QuoteNumber?: string;
  ToDate?: string;
  Vendor?: number;
  VendorProgram?: number[];
};

export interface BidRowList {
  bcn: string;
  bidEventHeaderId: string;
  bidId: number;
  bidNumber: string;
  currentStatus: string;
  customerSetId: number;
  id: number;
  parserHeaderID: string;
  quoteNumber: string;
  quoteStatus: string;
  resellerName: string;
  statusDateTimeUTC: string;
  totalCount: number;
  vendorName: string;
  vendorProgramName: string;
  version: number;
}

export interface BidSearchResponse {
  value: {
    pageDetails: {
      pageNumber: number;
      pageSize: number;
      totalPages: number;
      totalRecords: number;
    };
    response: {
      bidStatusList: BidRowList[];
    };
    responsePreamble: {
      message: string;
      statusCode: number;
    };
  };
}

export function useBLCDefaultBids(
  params: Pagination & { CompanyCode: string },
  enabled: boolean
) {
  const buildVars = useBuildVars();
  return useApiQuery<BidSearchResponse>(
    '/gde/v1/bidstatustracker/DefaultBids',
    params,
    { enabled },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useBLCDefaultBidsCount(enabled: boolean) {
  const buildVars = useBuildVars();
  return useApiQuery<{
    totalBidCount: number;
  }>(
    '/gde/v1/bidstatustracker/DefaultBidsCount',
    undefined,
    { enabled },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useBLCBidSearch(params: BidSearchRequest, enabled: boolean) {
  const buildVars = useBuildVars();
  return useApiQuery<BidSearchResponse>(
    '/gde/v1/bidstatustracker/SearchV3',
    params,
    {
      enabled,
    },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface BidsAVGTimesRequest {
  FromDate?: string;
  ToDate?: string;
  Vendor?: string;
  Version?: string;
}
export interface AVGTimes {
  acopTime: number;
  autoQuoteTime: number;
  importTime: number;
}
export interface BidsAVGTimesResponse {
  value: {
    response: AVGTimes;
    responsePreamble: {
      message: string;
      statusCode: number;
    };
  };
}

export function useBLCAVGTimes(params: BidsAVGTimesRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<BidsAVGTimesResponse>(
    '/gde/v1/bidstatustracker/GetAvgTimeMetrics',
    params,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface VendorList {
  isActive: boolean;
  vendorID: number;
  vendorName: string;
  vendorProgramName: string;
}
export interface StatusLIst {
  status: string;
  statusID: number;
}
export interface BidsLookupResponse {
  value: {
    response: {
      statusList: StatusLIst[];
      vendorList: VendorList[];
    };
    responsePreamble: {
      message: string;
      statusCode: number;
    };
  };
}

export function useBLCLookUpList() {
  const buildVars = useBuildVars();
  return useApiQuery<BidsLookupResponse>(
    '/gde/v1/bidstatustracker/GetDashboardLookupListsV2',
    undefined,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface BidDetailsRequest {
  bidEventHeaderId: string;
  customerSetId: number;
  id: number;
}

export interface BidDetail {
  createdBy: string;
  createdOn: string;
  id: number;
  masterID: number;
  modifiedOn: string;
  notes: string;
  parserHeaderID: string;
  prevReferenceID: string;
  referenceID: string;
  source: string;
  status: string;
  statusDateTime: string;
  statusDateTimeUTC: string;
  summary: string;
  url: string;
}

export interface BidDetailsResponse {
  value: {
    responseDetails: BidDetail[];
    responsePreamble: {
      message: string;
      statusCode: number;
    };
  };
}

export function useBLCBidDetails(params: BidDetailsRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<BidDetailsResponse>(
    '/gde/v1/bidstatustracker/GetBidStatusDetailsByID',
    params,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export const generateBidSearchQuery = (
  searchQuery: BidSearchQuery,
  pagination: Pagination,
  countryCode: string
) => {
  return {
    ...pagination,
    ...searchQuery,
    ...(searchQuery.CurrentStatus && {
      CurrentStatus: searchQuery.CurrentStatus?.toString(),
    }),
    ...(searchQuery.Vendor && {
      Vendor: searchQuery.Vendor?.toString(),
    }),
    ...(searchQuery.VendorProgram && {
      VendorProgram: searchQuery.VendorProgram?.toString(),
    }),
    ...(searchQuery.FromDate && {
      FromDate: `${searchQuery.FromDate}T00:00:00${
        countryCodeOffsetMapping[
          countryCode as keyof typeof countryCodeOffsetMapping
        ]
      }`,
    }),
    ...(searchQuery.ToDate && {
      ToDate: `${searchQuery.ToDate}T23:59:59${
        countryCodeOffsetMapping[
          countryCode as keyof typeof countryCodeOffsetMapping
        ]
      }`,
    }),
  };
};
